import React, { createContext, useContext, useEffect, useState } from 'react'
import { useQuery, ApolloError } from '@apollo/client'
import { getAssociationQuery } from '../queries/queries'
import { getDashboardAssociationBookletsQuery } from '../queries/booklet'
import { Association, Booklet } from '../types/generated.types'

interface DashboardAssociationContextType {
  associationId: string
  associationData: Association | undefined
  associationBooklets: Booklet[]
  shopShareDomain: string | undefined
  defaultBooklet: Booklet | undefined
  selectedBooklet: Booklet | undefined
  setSelectedBooklet: React.Dispatch<React.SetStateAction<Booklet | undefined>>
  completedOrderDirectSales: number | null
  setCompletedOrderDirectSales: React.Dispatch<React.SetStateAction<number | null>>
  completedOrderResellerSales: number | null
  setCompletedOrderResellerSales: React.Dispatch<React.SetStateAction<number | null>>
  loading: boolean
  error: ApolloError | undefined
}

const DashboardAssociationContext = createContext<DashboardAssociationContextType | undefined>(undefined)

interface DashboardAssociationProviderProps {
  associationId: string
  children: React.ReactNode
}

export const DashboardAssociationProvider: React.FC<DashboardAssociationProviderProps> = ({
  associationId,
  children
}) => {
  const { data: associationQueryData, loading: associationLoading, error: associationError } = useQuery(getAssociationQuery, { variables: { associationId } })
  const { data: dashboardBookletsData, loading: bookletsLoading, error: bookletsError } = useQuery(getDashboardAssociationBookletsQuery, { variables: { associationId } })

  const associationData = associationQueryData?.association
  const associationBooklets = dashboardBookletsData?.booklets || []

  const shopShareDomain = associationData?.shopShareDomain?.startsWith('http')
    ? associationData.shopShareDomain
    : `https://${associationData?.shopShareDomain}`;

  // Förvalt häfte är det senaste giltiga häftet
  const defaultBooklet = associationBooklets
    .filter((booklet) => booklet.validFrom)
    .sort((a, b) => new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime())[0]

  // Återställ valt häfte när man byter förening
  useEffect(() => {
    setSelectedBooklet(defaultBooklet);
  }, [associationId, defaultBooklet]);

  const [selectedBooklet, setSelectedBooklet] = useState<Booklet | undefined>(defaultBooklet);
  const [completedOrderDirectSales, setCompletedOrderDirectSales] = useState<number | null>(null);
  const [completedOrderResellerSales, setCompletedOrderResellerSales] = useState<number | null>(null);

  const loading = associationLoading || bookletsLoading
  const error = associationError || bookletsError

  const contextValue: DashboardAssociationContextType = {
    associationId,
    associationData,
    associationBooklets,
    shopShareDomain,
    defaultBooklet,
    selectedBooklet,
    setSelectedBooklet,
    completedOrderDirectSales,
    setCompletedOrderDirectSales,
    completedOrderResellerSales,
    setCompletedOrderResellerSales,
    loading,
    error
  }

  return (
    <DashboardAssociationContext.Provider value={contextValue}>
      {children}
    </DashboardAssociationContext.Provider>
  )
}

export const useDashboardAssociationContext = (): DashboardAssociationContextType => {
  const context = useContext(DashboardAssociationContext)
  if (context === undefined) {
    throw new Error('useDashboardAssociationContext must be used within a DashboardAssociationProvider')
  }
  return context
}
